import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ExpiringItem,
  ExpiringItemCategory,
  ExpiringItemsListResponse,
} from '@stfors/shared-types';
import { api } from '../client';

export interface ExpiringItemFilters {
  category?: ExpiringItemCategory;
  owner_user_id?: number;
  within_days?: number;
  include_expired?: boolean;
}

export function useExpiringItems(filters: ExpiringItemFilters = {}) {
  const params = new URLSearchParams();
  if (filters.category) params.set('category', filters.category);
  if (filters.owner_user_id !== undefined) params.set('owner_user_id', String(filters.owner_user_id));
  if (filters.within_days !== undefined) params.set('within_days', String(filters.within_days));
  if (filters.include_expired) params.set('include_expired', 'true');
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/expiring-items?${qs}` : '/api/expiring-items';
  return useQuery({
    queryKey: ['expiring-items', { ...filters }],
    queryFn: ({ signal }) => api.get<ExpiringItemsListResponse>(url, signal),
  });
}

export interface CreateExpiringItemInput {
  category: ExpiringItemCategory;
  name: string;
  owner_user_id?: number;
  issued_at?: number;
  expires_at: number;
  reminder_days_before?: number;
  notes?: string;
}

export function useCreateExpiringItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpiringItemInput) =>
      api.post<{ expiring_item: ExpiringItem }>('/api/expiring-items', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expiring-items'] });
    },
  });
}

export interface UpdateExpiringItemInput {
  id: number;
  name?: string;
  expires_at?: number;
  reminder_days_before?: number;
  notes?: string | null;
}

export function useUpdateExpiringItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateExpiringItemInput) =>
      api.patch<{ expiring_item: ExpiringItem }>(`/api/expiring-items/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expiring-items'] });
    },
  });
}

export function useDeleteExpiringItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/expiring-items/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expiring-items'] });
    },
  });
}
